import React, { useState, useEffect } from 'react';
import { Modal, Switch, Select, Text, Button, Group, Stack, Divider, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { SUPPORT_PHONE } from './paymentPackages';

// Поля карточки ученика (Contact) в CRM. Минуты общие для SMS и email
export const REMINDER_FIELDS = ['cReminderSms', 'cReminderEmail', 'cReminderDelay', 'cReminderMorningSms', 'cReminderMorningEmail'];
const DEFAULT_SETTINGS = {
  cReminderSms: true,
  cReminderEmail: true,
  cReminderDelay: 60,
  cReminderMorningSms: true,
  cReminderMorningEmail: true,
};

// CRM примет любое целое от 5 до 720, в списке — удобные варианты
const REMINDER_DELAYS = [15, 30, 60, 120, 180, 360, 720];

// Утреннее напоминание CRM шлёт в 7:00 по Нью-Йорку
const MORNING_HOUR_NY = 7;

// Пустое поле в CRM — считаем значением по умолчанию
export function pickReminderSettings(data) {
  return REMINDER_FIELDS.reduce((acc, field) => {
    acc[field] = data[field] ?? DEFAULT_SETTINGS[field];
    return acc;
  }, {});
}

// Попап «Настройка уведомлений» в шапке кабинета ученика.
// settings — сохранённые в CRM значения, contacts — { hasPhone, hasEmail }: без контакта канал не работает
function NotificationSettings({ apiBaseUrl, userId, timezone, settings, contacts, opened, onClose, onSaved }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Каждое открытие начинается с сохранённых значений
  useEffect(() => {
    if (opened) {
      setDraft(settings);
      setError(null);
    }
  }, [opened, settings]);

  if (!settings || !draft) return null;

  const setField = (field) => (value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    // PATCH частичный — шлём только изменённое
    const changes = REMINDER_FIELDS.reduce((acc, field) => {
      if (draft[field] !== settings[field]) acc[field] = draft[field];
      return acc;
    }, {});
    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/notifications/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
      });
      if (!response.ok) throw new Error(`Ошибка HTTP: ${response.status}`);
      onSaved(pickReminderSettings({ ...draft, ...(await response.json()) }));
      onClose();
    } catch (e) {
      console.error('Ошибка при сохранении настроек уведомлений:', e.message);
      setError(t('notifications.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const hasPhone = contacts.hasPhone !== false;
  const hasEmail = contacts.hasEmail !== false;
  const beforeLessonOff = !(hasPhone && draft.cReminderSms) && !(hasEmail && draft.cReminderEmail);

  // Выставленное менеджером значение не из списка тоже показываем
  const delays = REMINDER_DELAYS.includes(draft.cReminderDelay)
    ? REMINDER_DELAYS
    : [...REMINDER_DELAYS, draft.cReminderDelay].sort((a, b) => a - b);
  const delayLabel = (minutes) => (minutes % 60 === 0
    ? t('notifications.hoursOption', { count: minutes / 60 })
    : t('notifications.minutesOption', { count: minutes }));

  const morningNy = DateTime.now().setZone('America/New_York').set({ hour: MORNING_HOUR_NY, minute: 0 });
  const morningLocal = morningNy.setZone(timezone);
  const morningDescription = morningLocal.offset === morningNy.offset
    ? t('notifications.morningDescription')
    : t('notifications.morningDescriptionLocal', { time: morningLocal.toFormat('HH:mm') });

  // Канал без телефона/email: выключен и неактивен, в CRM значение не трогаем
  const channelSwitch = (field, label, available, missingHint) => (
    <Switch
      label={label}
      description={available ? null : missingHint}
      checked={available && draft[field]}
      disabled={!available || saving}
      onChange={(e) => setField(field)(e.currentTarget.checked)}
    />
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700} size="lg">{t('notifications.title')}</Text>}
      centered
      radius="lg"
    >
      <Stack gap="lg">
        <Stack gap="sm">
          <Text fw={700}>{t('notifications.beforeLessonTitle')}</Text>
          {channelSwitch('cReminderSms', t('notifications.sms'), hasPhone, t('notifications.noPhone'))}
          {channelSwitch('cReminderEmail', t('notifications.email'), hasEmail, t('notifications.noEmail'))}
          <Select
            label={t('notifications.minutesLabel')}
            data={delays.map((m) => ({ value: String(m), label: delayLabel(m) }))}
            value={String(draft.cReminderDelay)}
            onChange={(value) => value && setField('cReminderDelay')(Number(value))}
            disabled={beforeLessonOff || saving}
            allowDeselect={false}
          />
        </Stack>

        <Divider />

        <Stack gap="sm">
          <div>
            <Text fw={700}>{t('notifications.morningTitle')}</Text>
            <Text size="sm" c="dimmed">{morningDescription}</Text>
          </div>
          {channelSwitch('cReminderMorningSms', t('notifications.sms'), hasPhone, t('notifications.noPhone'))}
          {channelSwitch('cReminderMorningEmail', t('notifications.email'), hasEmail, t('notifications.noEmail'))}
        </Stack>

        <Text size="sm" c="dimmed" p="sm" style={{ background: 'rgba(14, 165, 233, 0.06)', borderRadius: 12 }}>
          {t('notifications.contactManager')}{' '}
          <a href={SUPPORT_PHONE.href} style={{ color: 'var(--mantine-color-brand-7)', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            {SUPPORT_PHONE.label}
          </a>
        </Text>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={saving}>
            {t('notifications.cancel')}
          </Button>
          <Button onClick={handleSave} loading={saving}>
            {t('notifications.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default NotificationSettings;
